import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { MainContext } from 'utils/context';
import { 
  getQuizsAll, 
  fetchQuizzesFromDatabase, 
  getDataSubmit,
  getAllUsers,
  updateUserData,
  deleteUserData,
  deleteQuizData,
  updateQuizData
} from 'utils/firebaseFunction';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

const Admin = () => {
  const { userId } = useContext(MainContext);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    totalQuestions: 0,
    totalUsers: 0
  });
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      if (userId !== 'JuAxp52a7uMSF7vrzwxKVpIN6Nw2') {
        navigate('/');
        return;
      }
      fetchData();
    };

    checkAdmin();
  }, [userId, navigate]);

  const fetchData = async () => {
    try {
      // جلب الكويزات
      const quizzesData = await fetchQuizzesFromDatabase();
      setQuizzes(quizzesData);
      
      // جلب المستخدمين
      const usersData = await getAllUsers();
      setUsers(usersData);
      
      // حساب الإحصائيات
      setStats({
        totalQuizzes: quizzesData.length,
        totalQuestions: quizzesData.reduce((acc, quiz) => acc + (quiz.questions?.length || 0), 0),
        totalUsers: usersData.length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الاختبار؟')) {
      try {
        const result = await deleteQuizData(quizId);
        if (result.success) {
          setQuizzes(quizzes.filter(quiz => quiz.id !== quizId));
          setStats(prev => ({
            ...prev,
            totalQuizzes: prev.totalQuizzes - 1
          }));
        }
      } catch (error) {
        console.error('Error deleting quiz:', error);
      }
    }
  };

  const handleEditQuiz = async (quizId) => {
    try {
      navigate(`/edit-quiz/${quizId}`);
    } catch (error) {
      console.error('Error navigating to edit quiz:', error);
    }
  };

  const handleUpdateUser = async (userId, updatedData) => {
    try {
      const result = await updateUserData(userId, updatedData);
      if (result.success) {
        setUsers(users.map(user => 
          user.id === userId ? { ...user, ...updatedData } : user
        ));
        setEditingUser(null);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) {
      try {
        const result = await deleteUserData(userId);
        if (result.success) {
          setUsers(users.filter(user => user.id !== userId));
          setStats(prev => ({
            ...prev,
            totalUsers: prev.totalUsers - 1
          }));
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleUpdateUserRole = async (userId, currentIsAdmin) => {
    if (window.confirm(currentIsAdmin ? 'هل تريد إلغاء صلاحيات الأدمن؟' : 'هل تريد منح صلاحيات الأدمن؟')) {
      try {
        const result = await updateUserData(userId, { isAdmin: !currentIsAdmin });
        if (result.success) {
          setUsers(users.map(user => 
            user.id === userId ? { ...user, isAdmin: !currentIsAdmin } : user
          ));
        }
      } catch (error) {
        console.error('Error updating user role:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <LoadingSpinner size="large" text="جاري تحميل لوحة التحكم..." />
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>لوحة التحكم</h1>
        <p>مرحباً بك في لوحة تحكم الأدمن</p>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <h3>إجمالي الاختبارات</h3>
          <div className="stat-value">{stats.totalQuizzes}</div>
        </div>
        <div className="stat-card">
          <h3>إجمالي الأسئلة</h3>
          <div className="stat-value">{stats.totalQuestions}</div>
        </div>
        <div className="stat-card">
          <h3>إجمالي المستخدمين</h3>
          <div className="stat-value">{stats.totalUsers}</div>
        </div>
      </div>

      <div className="admin-actions">
        <div className="action-card">
          <h3>إنشاء اختبار جديد</h3>
          <p>قم بإنشاء اختبار جديد مع إضافة الأسئلة والخيارات</p>
          <button className="action-button" onClick={() => navigate('/addQuiz')}>
            إنشاء اختبار
          </button>
        </div>
        <div className="action-card">
          <h3>إدارة المستخدمين</h3>
          <p>عرض وإدارة المستخدمين المسجلين في النظام</p>
          <button 
            className="action-button" 
            onClick={() => setShowUsers(!showUsers)}
          >
            {showUsers ? 'إخفاء المستخدمين' : 'عرض المستخدمين'}
          </button>
        </div>
      </div>

      {showUsers && (
        <>
          <div className="section-title">
            <h2>إدارة المستخدمين</h2>
          </div>
          <div className="admin-table users-table">
            <table>
              <thead>
                <tr>
                  <th>اسم المستخدم</th>
                  <th>البريد الإلكتروني</th>
                  <th>تاريخ التسجيل</th>
                  <th>نوع المستخدم</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.createdAt?.toDate().toLocaleDateString('ar-SA')}</td>
                    <td>
                      <span className={`user-type ${user.isAdmin ? 'admin' : 'user'}`}>
                        {user.isAdmin ? 'أدمن' : 'مستخدم عادي'}
                      </span>
                    </td>
                    <td className="action-cell">
                      <button 
                        className={`role-btn ${user.isAdmin ? 'remove-admin' : 'make-admin'}`}
                        onClick={() => handleUpdateUserRole(user.id, user.isAdmin)}
                      >
                        {user.isAdmin ? '  مستخدم' : '  ادمن'}
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* قسم الاختبارات */}
      <div className="section-title">
        <h2>إدارة الاختبارات</h2>
      </div>
      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>عنوان الاختبار</th>
              <th>عدد الأسئلة</th>
              <th>تاريخ الإنشاء</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {quizzes.map(quiz => (
              <tr key={quiz.id}>
                <td>
                  {editingQuiz?.id === quiz.id ? (
                    <input
                      type="text"
                      value={editingQuiz.title}
                      onChange={(e) => setEditingQuiz({ ...editingQuiz, title: e.target.value })}
                    />
                  ) : (
                    quiz.title
                  )}
                </td>
                <td>{quiz.questions?.length || 0}</td>
                <td>{new Date(quiz.createdAt?.toDate()).toLocaleDateString('ar-SA')}</td>
                <td className="action-cell">
                  {editingQuiz?.id === quiz.id ? (
                    <>
                      <button 
                        className="save-btn"
                        onClick={() => handleEditQuiz(quiz.id)}
                      >
                        تعديل
                      </button>
                      <button 
                        className="cancel-btn"
                        onClick={() => setEditingQuiz(null)}
                      >
                        إلغاء
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditQuiz(quiz.id)}
                      >
                        تعديل
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteQuiz(quiz.id)}
                      >
                        حذف
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;